import {type ImagePickerAsset} from 'expo-image-picker'

import {VIDEO_MAX_SIZE} from '#/lib/constants'
import {VideoTooLargeError} from '#/lib/media/video/errors'
import {type CompressedVideo} from './types'

// Large file threshold - files above this size won't be loaded into memory
const LARGE_FILE_THRESHOLD = 100 * 1024 * 1024 // 100MB
// Skip compression for videos longer than 3 minutes - compression causes errors on longer videos
const MAX_DURATION_FOR_COMPRESSION_MS = 3 * 60 * 1000 // 3 minutes

// doesn't actually compress, converts to ArrayBuffer for small files, uses blob URI for large files
export async function compressVideo(
  asset: ImagePickerAsset,
  _opts?: {
    signal?: AbortSignal
    onProgress?: (progress: number) => void
  },
): Promise<CompressedVideo> {
  // Skip compression for videos longer than 3 minutes - compression causes errors
  // Web doesn't actually compress, but we still need to handle the duration check
  // to be consistent with native behavior
  const duration = asset.duration ?? 0
  if (duration > MAX_DURATION_FOR_COMPRESSION_MS) {
    // For long videos, just prepare the file for upload without any processing
    // This matches native behavior where we skip compression
    // Fetch the blob directly and return it without loading into memory
    let blob: Blob
    let uri: string
    let mimeType: string
    
    if (asset.uri.startsWith('blob:')) {
      const response = await fetch(asset.uri)
      blob = await response.blob()
      uri = asset.uri
      mimeType = asset.mimeType || blob.type || 'video/mp4'
    } else if (asset.uri.startsWith('data:')) {
      try {
        const parsed = parseDataUrl(asset.uri)
        blob = base64ToBlob(parsed.base64, parsed.mimeType)
        uri = URL.createObjectURL(blob)
        mimeType = parsed.mimeType
      } catch (e) {
        const response = await fetch(asset.uri)
        blob = await response.blob()
        uri = URL.createObjectURL(blob)
        mimeType = asset.mimeType || blob.type || 'video/mp4'
      }
    } else {
      const response = await fetch(asset.uri)
      blob = await response.blob()
      uri = URL.createObjectURL(blob)
      mimeType = asset.mimeType || blob.type || 'video/mp4'
    }
    
    if (blob.size > VIDEO_MAX_SIZE) {
      throw new VideoTooLargeError()
    }
    
    // Don't load large files into memory
    return {
      size: blob.size,
      uri,
      bytes: undefined, // Don't load into memory for long videos
      mimeType,
    }
  }
  
  let blob: Blob
  let uri: string
  let mimeType: string

  // Handle different URI types from expo-image-picker
  try {
    if (asset.uri.startsWith('blob:')) {
      // Large files come as blob: URLs - fetch the blob directly
      const response = await fetch(asset.uri)
      blob = await response.blob()
      uri = asset.uri // Use the original blob URL
      mimeType = asset.mimeType || blob.type || 'video/mp4'
    } else if (asset.uri.startsWith('data:')) {
      // Small files come as data: URLs with base64
      // For large files, base64 might be missing or invalid, so handle gracefully
      try {
        const parsed = parseDataUrl(asset.uri)
        blob = base64ToBlob(parsed.base64, parsed.mimeType)
        uri = URL.createObjectURL(blob)
        mimeType = parsed.mimeType
      } catch (e) {
        // If data URL parsing fails (e.g., large file with missing base64),
        // try to fetch the original file if we have a fallback
        // Otherwise, try to get the file from the asset directly
        throw new Error(`Failed to process video file. The file may be too large or corrupted. ${e instanceof Error ? e.message : String(e)}`)
      }
    } else {
      // Fallback: try to fetch as blob
      const response = await fetch(asset.uri)
      blob = await response.blob()
      uri = URL.createObjectURL(blob)
      mimeType = asset.mimeType || blob.type || 'video/mp4'
    }
  } catch (e) {
    // If all else fails, provide a helpful error message
    const errorMsg = e instanceof Error ? e.message : String(e)
    throw new Error(`Failed to load video file: ${errorMsg}. Please try selecting the file again.`)
  }

  if (blob.size > VIDEO_MAX_SIZE) {
    throw new VideoTooLargeError()
  }

  // For large files, don't load into memory - upload will use the blob directly
  // Only load small files into memory for compatibility
  const shouldLoadIntoMemory = blob.size < LARGE_FILE_THRESHOLD

  return {
    size: blob.size,
    uri,
    // Only provide bytes for small files to avoid memory issues
    bytes: shouldLoadIntoMemory ? await blob.arrayBuffer() : undefined,
    mimeType,
  }
}

function parseDataUrl(dataUrl: string) {
  const [mimeType, base64] = dataUrl.slice('data:'.length).split(';base64,')
  if (!mimeType || !base64) {
    throw new Error('Invalid data URL')
  }
  return {mimeType, base64}
}

function base64ToBlob(base64: string, mimeType: string) {
  const byteCharacters = atob(base64)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512)
    const byteNumbers = new Array(slice.length)

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, {type: mimeType})
}
